import { colors } from 'bench-style-constants';
import { Icon, TextBlock } from 'bench-internal-theme';
import React, { PureComponent } from 'react';
import styled from 'styled-components';

import type { DataEntry, LedgerAccount } from './types';
import DataEntryTableSelectAllButton from './DataEntryTableSelectAllButton';
import { Client } from './GeneralLedger';

const DataEntryHeaderRowLayout = styled(TextBlock).attrs(() => ({
  color: colors.smoke,
  styleType: 'small',
  uppercase: true,
}))`
  align-items: center;
  background-color: ${colors.pearl};
  border-top: 1px solid ${colors.silt};
  // Ensures there's a "bottom border", but doesn't change the height
  box-shadow: 0 1px 0 ${colors.silt};
  display: flex;
  height: 33px;
  margin: 0 32px;
`;

const IconCell = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  flex-basis: 28px;
  min-width: 28px;
`;

const FilterCell = styled.div`
  padding-right: 8px;
  min-width: 32px;
`;

const Columns1099Cell = styled.div`
  display: flex;
  flex-basis: 80px;
  min-width: 80px;
  padding: 0 8px;
`;

const NameWrapper = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColumnCell = styled.div<{
  width?: string;
  align?: string;
  maxWidth?: string;
  minWidth?: string;
}>`
  ${({ width, minWidth }) =>
    width === 'auto'
      ? `
        flex: 1;
        min-width: ${minWidth || '0'};
      `
      : `
        flex-basis: ${width};
        min-width: ${width};
        max-width: ${width};
  `};
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ align }) => align && `text-align: ${align}`};
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth}`};
`;

type HeadingColumn = {
  align?: string;
  heading: string;
  width?: string | 'auto';
  maxWidth?: string;
  minWidth?: string;
  icon?: string;
  sortKey?: string;
  getColumnVisibility?: (ledgerAccount: LedgerAccount) => boolean;
};

type DataEntryHeaderRowProps = {
  columns: Array<HeadingColumn>;
  dataEntriesUnderHeader: Array<DataEntry>;
  ledgerAccount?: LedgerAccount;
  client?: Client;
  onSortChange: (sortKey: string, sortDirection: 'asc' | 'desc') => void;
  sortState: { key: string; direction: 'asc' | 'desc' };
  filterMito: boolean;
  onMitoFilterChange: () => void;
};

class DataEntryHeaderRow extends PureComponent<DataEntryHeaderRowProps> {
  static defaultProps = {
    ledgerAccount: undefined,
  };

  static getHeight() {
    return 33;
  }

  handleSort = (heading: string) => {
    const { sortState, onSortChange, columns } = this.props;
    const column = columns.find(col => col.heading === heading);
    if (!column || !column.sortKey) {
      return;
    }
    const newDirection =
      sortState.key === column.sortKey && sortState.direction === 'asc' ? 'desc' : 'asc';
    onSortChange(column.sortKey, newDirection); // Use the key for sorting
  };

  handleFilterMito = () => {
    const { onMitoFilterChange } = this.props;
    onMitoFilterChange();
  };

  private getSortIndicator(sortKey: string | undefined): string {
    const { sortState } = this.props;
    if (!sortKey) return '';
    if (sortKey === sortState?.key) {
      return sortState.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return ' ⇅';
  }

  render() {
    const { columns, dataEntriesUnderHeader, ledgerAccount, client, filterMito } = this.props;
    const report1099ClientConfig = client?.featureFlags.report1099;

    return (
      <DataEntryHeaderRowLayout>
        <IconCell>
          {dataEntriesUnderHeader.length > 0 && (
            <DataEntryTableSelectAllButton
              controlledDataEntryIds={dataEntriesUnderHeader.map(({ id }) => id)}
            />
          )}
        </IconCell>
        {columns.map(
          ({ align, heading, icon, width, maxWidth, minWidth, getColumnVisibility, sortKey }) => {
            const isVisible =
              typeof getColumnVisibility === 'function' ? getColumnVisibility(ledgerAccount) : true;

            if (!isVisible) {
              return null;
            }

            // Hide the 1099 project header columns if flag is not enabled.
            if (['Contractor', 'Sources'].includes(heading)) {
              return report1099ClientConfig ? (
                <Columns1099Cell key={heading}>
                  <NameWrapper>{heading}</NameWrapper>
                </Columns1099Cell>
              ) : null;
            }

            if (heading === 'Toggle filtering for transactions with potential matches') {
              return (
                <FilterCell
                  key={heading}
                  title={heading}
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  <Icon
                    color={filterMito ? colors.black : colors.clay}
                    icon={icon}
                    size="small"
                    cursor="pointer"
                    onClick={() => this.handleFilterMito()}
                  />
                </FilterCell>
              );
            }

            if (icon) {
              return (
                <IconCell key={heading} title={heading}>
                  <Icon color={colors.clay} icon={icon} size="small" />
                </IconCell>
              );
            }
            return (
              <ColumnCell
                align={align}
                key={heading}
                width={width}
                maxWidth={maxWidth}
                minWidth={minWidth}
                onClick={sortKey ? () => this.handleSort(heading) : undefined}
                style={{
                  cursor: sortKey ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                }}
              >
                {heading}
                {this.getSortIndicator(sortKey)}
              </ColumnCell>
            );
          },
        )}
      </DataEntryHeaderRowLayout>
    );
  }
}

export default DataEntryHeaderRow;
